import React from "react";
import SEOComponent from "../components/SEOComponent";
import Navigation from "../components/Navigation";
import { resetConsent } from "../lib/analytics";

const CONTACT_EMAIL = "hello@rebelheat.co.uk";
const DELETION_FORM =
  "https://docs.google.com/forms/d/e/1FAIpQLSdR8gCU7R09XEFloyo6QtweoVSKt2J301AP6XeoWZTu2WvDLg/viewform?usp=dialog";

const H2 = ({ children }) => (
  <h2 className="text-2xl font-semibold text-rebel-dark mt-10 mb-4">{children}</h2>
);
const H3 = ({ children }) => (
  <h3 className="text-lg font-semibold text-rebel-dark mt-6 mb-2">{children}</h3>
);
const List = ({ items }) => (
  <ul className="list-disc pl-6 mb-6 space-y-2">
    {items.map((item, i) => (
      <li key={i}>{item}</li>
    ))}
  </ul>
);

const uses = [
  {
    purpose: "Run your account, the app and your sensor, and show you your heating data",
    data: "Account, home, sensor and energy data",
    basis: "Contract (providing the service you asked for)",
  },
  {
    purpose: "Fit a sensor for a free heat pump sizing, and reply to waitlist and sizing requests",
    data: "Details you give us in our forms",
    basis: "Steps you asked us to take before a contract; legitimate interests (answering enquiries)",
  },
  {
    purpose: "Let a home owner invite other people (residents, installers) to see a home",
    data: "Invitee email address and role",
    basis: "Legitimate interests (sharing a home's heating data with the people who look after it)",
  },
  {
    purpose: "Improve our analysis, sensors and service",
    data: "Sensor and energy data",
    basis: "Legitimate interests (developing our products)",
  },
  {
    purpose: "Understand how people use this website",
    data: "Website usage data",
    basis: "Consent (you can refuse or withdraw it at any time)",
  },
];

const Privacy = () => (
  <>
    <SEOComponent
      title="Privacy Policy | Rebel Heat"
      description="How Rebel Heat collects and uses personal data on its website, app and sensors"
      url="https://www.rebelheat.co.uk/privacy"
      image="https://www.rebelheat.co.uk/logo.png"
    />
    <div className="min-h-screen bg-white">
      <Navigation />
      <div className="container-max px-4 py-20">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-4xl font-bold text-rebel-dark mb-4">Privacy Policy</h1>
          <p className="text-sm text-gray-500 mb-8">Last updated: September 2026</p>

          <div className="prose prose-gray max-w-none text-gray-600">
            <p className="mb-4">
              This policy explains how Rebel Heat Ltd ("we", "us") uses personal data when you visit
              www.rebelheat.co.uk, fill in one of our forms, use the Rebel Heat app or have one of our
              sensors fitted to your heating system. It also covers our Data Portal for business customers.
            </p>
            <p className="mb-6">
              Rebel Heat Ltd (company number 15664690) is the data
              controller. You can contact us about anything in this policy at{" "}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-rebel-coral">{CONTACT_EMAIL}</a>.
            </p>

            <H2>What we collect</H2>

            <H3>When you visit the website</H3>
            <List
              items={[
                "If you accept analytics cookies: pages you view, how you got here, and your device, browser and approximate location.",
                "Your IP address, which is needed to deliver the page to you, including parts like fonts and forms.",
              ]}
            />

            <H3>When you fill in a form</H3>
            <p className="mb-6">
              Our waitlist and heat pump sizing forms ask for things like your name, email address,
              phone number, address and some details about your home and boiler. If we fit a sizing
              sensor, we also arrange a visit to your home.
            </p>

            <H3>When you use the app</H3>
            <List
              items={[
                "Account: your email address and password (we never see your password).",
                "Your home: a name for it and its location, which you type in or take from your phone's location. We use the location to match your heating to local weather.",
                "People in your home: if you invite someone, their email address and their role (for example resident or installer).",
                "Orders and trials: when you ask to order or trial a sensor, we record that and send ourselves an email with your email address and home location so we can get in touch.",
                "Wi-Fi: the network name and password you give the sensor during setup are stored only on the sensor, never on our servers.",
              ]}
            />

            <H3>From your sensor</H3>
            <p className="mb-6">
              The sensor measures your boiler's flow and return pipe temperatures, the room temperature
              and the sound the boiler makes. For sound, the sensor only sends a summary of sound levels
              at different pitches, which is enough to tell when the boiler is firing. It never records
              or sends audio. We link these readings to your home.
            </p>

            <H3>If you connect your energy supplier</H3>
            <p className="mb-6">
              If you connect your energy supplier account, we store the access details you give us
              (encrypted) and your gas meter details. We use them to download up to two years of
              half-hourly gas usage for your home, so we can see how your heating relates to your gas
              use. You can disconnect this at any time.
            </p>

            <H3>If you use the Data Portal</H3>
            <p className="mb-6">
              Business customers sign in with an email address, and we store data from the heating,
              solar and battery equipment we monitor or control for them.
            </p>

            <H2>How we use it</H2>
            <div className="overflow-x-auto mb-6">
              <table className="min-w-full border border-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left border-b">Purpose</th>
                    <th className="px-4 py-2 text-left border-b">Data</th>
                    <th className="px-4 py-2 text-left border-b">Legal basis</th>
                  </tr>
                </thead>
                <tbody>
                  {uses.map((u, i) => (
                    <tr key={i} className="border-b">
                      <td className="px-4 py-2">{u.purpose}</td>
                      <td className="px-4 py-2">{u.data}</td>
                      <td className="px-4 py-2">{u.basis}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mb-6">
              The app can give you written advice about your heating. To produce it, we send a summary
              of your heating and gas data (not your name, email address or location) to an AI
              model. The advice is for information
              only. We don't make any decisions about you automatically.
            </p>
            <p className="mb-6">We don't sell your data and we don't use it for advertising.</p>

            <H2>Who we share it with</H2>
            <p className="mb-4">
              We use trusted service providers to run our service. They only use your data on our
              instructions, and only for:
            </p>
            <List
              items={[
                "Hosting, storage, sign-in and sending notifications.",
                "Our online forms.",
                "Website analytics, if you accept cookies.",
                "Weather data for your area. We share your home's location, not who you are.",
                "Heating advice from an AI model, as described above.",
                "Fetching your gas usage from your energy supplier, if you connect your account.",
              ]}
            />
            <p className="mb-6">
              We may also share data with professional advisers, or where the law requires us to.
            </p>

            <H2>Where your data is stored</H2>
            <p className="mb-6">
              Most of your data is stored in the UK. Where a provider processes it outside the UK, we
              make sure it is protected to UK standards, for example by using countries the UK
              recognises as adequate or approved data transfer agreements.
            </p>

            <H2>How long we keep it</H2>
            <List
              items={[
                "Your account, home location and energy supplier details: until you delete your account (or remove the home or disconnect your supplier).",
                "When you delete your account, we delete your sign-in, and any home that has no other members, including its location and energy supplier details. We keep a record that you were a member of a home, including your email address, so there is a history of who could access it.",
                "Sensor readings and heating data: kept after an account is deleted, to improve our analysis. If you want these deleted too, email us.",
                "Form submissions: up to 2 years after we last hear from you, unless you become a customer.",
                "Website analytics: up to 14 months.",
              ]}
            />

            <H2>Your rights</H2>
            <p className="mb-4">
              You have the right to access, correct or delete your data, to ask us to limit or stop
              using it, to object to how we use it, to get a copy to take elsewhere, and to withdraw
              consent at any time. Email us at{" "}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-rebel-coral">{CONTACT_EMAIL}</a>. It's
              free, and we'll reply within one month. We may ask you to confirm who you are first.
            </p>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 my-6 text-center">
              <h4 className="text-lg font-semibold text-rebel-dark mb-2">Delete your data</h4>
              <p className="mb-4">
                You can delete your account in the app. You can also use our deletion request form or
                email us, and we'll deal with your request within one month.
              </p>
              <a
                href={DELETION_FORM}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-rebel-coral text-white px-6 py-2 rounded-lg hover:opacity-90 transition-opacity font-medium"
              >
                Request deletion
              </a>
            </div>

            <p className="mb-6">
              If you're unhappy with how we've handled your data, please tell us first. You can also
              complain to the Information Commissioner's Office at ico.org.uk or on 0303 123 1113.
            </p>

            <H2>Cookies</H2>
            <p className="mb-4">
              We only set analytics cookies if you click Accept on our cookie banner. Signing in to the
              Data Portal and remembering your cookie choice use your browser's storage, which is needed
              for the site to work. Our embedded forms may set cookies they need to work.
            </p>
            <button
              type="button"
              onClick={resetConsent}
              className="mb-6 px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-rebel-dark hover:bg-gray-50"
            >
              Change cookie settings
            </button>

            <H2>Children</H2>
            <p className="mb-6">
              Our services are for adults. We don't knowingly collect data from anyone under 18.
            </p>

            <H2>Changes to this policy</H2>
            <p className="mb-6">
              If we change this policy we'll update the date at the top, and if the change is significant
              and you have an account we'll email you.
            </p>
          </div>
        </div>
      </div>
    </div>
  </>
);

export default Privacy;
